import type { NexxusDatabase } from '@infra/persistence/NexxusDatabase';
import { sha256 } from '@shared/utils/hash';
import { exportBackupData, importBackupData } from '@infra/backup/BackupDataSource';
import { getAppVersion } from '@shared/constants/appVersion';

/** Réécrit tenant_id (et id des fiches entreprise) vers le dossier courant. */
export function remapBackupTenant(
  data: Record<string, unknown[]>,
  fromTenantId: string,
  toTenantId: string,
): Record<string, unknown[]> {
  if (fromTenantId === toTenantId) return data;
  const next: Record<string, unknown[]> = {};
  for (const [table, rows] of Object.entries(data)) {
    next[table] = rows.map((row) => {
      if (!row || typeof row !== 'object') return row;
      const rec = { ...(row as Record<string, unknown>) };
      if (rec.tenant_id === fromTenantId) rec.tenant_id = toTenantId;
      if (table === 'tenants' && rec.id === fromTenantId) rec.id = toTenantId;
      if (table === 'module_data') {
        const ns = typeof rec.namespace === 'string' ? rec.namespace : '';
        if (ns) {
          rec.id = `${toTenantId}::${ns}`;
        } else if (typeof rec.id === 'string' && rec.id.startsWith(`${fromTenantId}::`)) {
          rec.id = `${toTenantId}::${rec.id.slice(fromTenantId.length + 2)}`;
        }
      }
      return rec;
    });
  }
  return next;
}

/** La sauvegarde appartient à une autre entreprise que le dossier cible (P0-4). */
export class BackupTenantMismatchError extends Error {
  readonly code = 'BACKUP_TENANT_MISMATCH';
  constructor(
    readonly backupTenantId: string,
    readonly targetTenantId: string,
    readonly backupCompanyName: string | null,
  ) {
    super(
      `Cette sauvegarde appartient à une autre entreprise${
        backupCompanyName ? ` (« ${backupCompanyName} »)` : ''
      } et ne peut pas être restaurée dans le dossier courant. Ouvrez d’abord l’entreprise concernée.`,
    );
    this.name = 'BackupTenantMismatchError';
  }
}

/** Nom de l'entreprise inscrit dans la sauvegarde (fiche `tenants`), pour les messages. */
export function backupCompanyName(bundle: BackupBundle): string | null {
  const row = bundle.data.tenants?.[0] as
    | { raison_sociale?: unknown; nom?: unknown }
    | undefined;
  const name = row?.raison_sociale ?? row?.nom;
  return typeof name === 'string' && name.trim() ? name.trim() : null;
}

/**
 * Données à écrire vers l'entreprise cible. Transfert d'entreprise (opt-in explicite) : les lignes
 * sont rattachées à la cible, mais la fiche `tenants` de la cible n'est JAMAIS réécrite.
 */
export function backupDataForTarget(
  bundle: BackupBundle,
  targetTenantId: string,
): Record<string, unknown[]> {
  if (bundle.manifest.tenant_id === targetTenantId) return bundle.data;
  const remapped = remapBackupTenant(bundle.data, bundle.manifest.tenant_id, targetTenantId);
  return Object.fromEntries(Object.entries(remapped).filter(([table]) => table !== 'tenants'));
}

export const COFFREFORT_BACKUP_NOTE =
  'Les pièces binaires du coffre-fort restent dans le dossier utilisateur ; seul l’index (noms, chemins) est inclus dans cette sauvegarde.';

export interface BackupManifest {
  app_version: string;
  schema_version: number;
  date: string;
  tenant_id: string;
  counts: Record<string, number>;
  sha256: string;
  integrity_scope?: 'data' | 'manifest-and-data';
  coffrefort_scope?: 'index_only';
  coffrefort_note?: string;
}

export interface BackupBundle {
  manifest: BackupManifest;
  data: Record<string, unknown[]>;
}

/**
 * Service de sauvegarde et restauration des données.
 * Format : JSON UTF-8 + manifest avec compteurs et hash SHA-256.
 * Chiffrement optionnel AES-GCM avec mot de passe utilisateur.
 */
export class BackupService {
  static readonly SCHEMA_VERSION = 1;
  static readonly APP_VERSION = getAppVersion();

  constructor(private readonly db: NexxusDatabase) {}

  /** Génère un bundle de sauvegarde complet pour le tenant courant. */
  async export(tenant_id: string): Promise<BackupBundle> {
    const { data, counts } = await exportBackupData(this.db, tenant_id);
    const unsignedManifest: Omit<BackupManifest, 'sha256'> = {
      app_version: BackupService.APP_VERSION,
      schema_version: BackupService.SCHEMA_VERSION,
      date: new Date().toISOString(),
      tenant_id,
      counts,
      integrity_scope: 'manifest-and-data',
      coffrefort_scope: 'index_only',
      coffrefort_note: COFFREFORT_BACKUP_NOTE,
    };
    const hash = await sha256(JSON.stringify({ manifest: unsignedManifest, data }));
    const manifest: BackupManifest = { ...unsignedManifest, sha256: hash };
    return { manifest, data };
  }

  /** Sérialise le bundle pour téléchargement (JSON ou chiffré). */
  async toBlob(bundle: BackupBundle, password?: string): Promise<Blob> {
    const json = JSON.stringify(bundle, null, 2);
    if (!password) return new Blob([json], { type: 'application/json' });
    const encrypted = await this.encrypt(json, password);
    return new Blob([new Uint8Array(encrypted)], { type: 'application/octet-stream' });
  }

  /** Restaure un bundle vers la base. Vérifie le hash avant d'écrire. */
  async import(
    bundle: BackupBundle,
    opts?: { targetTenantId?: string; atomic?: boolean; allowTenantTransfer?: boolean },
  ): Promise<{ restored: Record<string, number>; integrity: boolean }> {
    if (!(await this.verify(bundle))) {
      return { restored: {}, integrity: false };
    }
    const mismatch = Boolean(
      opts?.targetTenantId && opts.targetTenantId !== bundle.manifest.tenant_id,
    );
    if (mismatch && !opts?.allowTenantTransfer) {
      // Jamais de réécriture silencieuse vers une autre entreprise : rien n'est écrit.
      throw new BackupTenantMismatchError(
        bundle.manifest.tenant_id,
        opts?.targetTenantId ?? '',
        backupCompanyName(bundle),
      );
    }
    const data = mismatch
      ? backupDataForTarget(bundle, opts?.targetTenantId ?? '')
      : bundle.data;
    const restored = await importBackupData(this.db, data, { atomic: opts?.atomic });
    return { restored, integrity: true };
  }

  async verify(bundle: BackupBundle): Promise<boolean> {
    if (!bundle?.manifest || !bundle.data || typeof bundle.data !== 'object') return false;
    if (bundle.manifest.schema_version !== BackupService.SCHEMA_VERSION) return false;
    if (!bundle.manifest.tenant_id || !/^[0-9a-f]{64}$/.test(bundle.manifest.sha256)) return false;

    for (const [table, expected] of Object.entries(bundle.manifest.counts)) {
      const rows = bundle.data[table];
      if (!Array.isArray(rows) || rows.length !== expected) return false;
      if (table === 'tenants') {
        if (rows.some((row) => (row as { id?: unknown }).id !== bundle.manifest.tenant_id)) {
          return false;
        }
      } else if (
        rows.some(
          (row) =>
            typeof row !== 'object' ||
            row === null ||
            (row as { tenant_id?: unknown }).tenant_id !== bundle.manifest.tenant_id,
        )
      ) {
        return false;
      }
    }

    const computedHash =
      bundle.manifest.integrity_scope === 'manifest-and-data'
        ? await this.hashManifestAndData(bundle)
        : await sha256(JSON.stringify(bundle.data));
    return computedHash === bundle.manifest.sha256;
  }

  private async hashManifestAndData(bundle: BackupBundle): Promise<string> {
    const unsignedManifest = Object.fromEntries(
      Object.entries(bundle.manifest).filter(([key]) => key !== 'sha256'),
    );
    return sha256(JSON.stringify({ manifest: unsignedManifest, data: bundle.data }));
  }

  /** Décode un bundle depuis un fichier (JSON ou chiffré). */
  async fromBlob(blob: Blob, password?: string): Promise<BackupBundle> {
    const buf = new Uint8Array(await blob.arrayBuffer());
    let json: string;
    if (password) {
      json = await this.decrypt(buf, password);
    } else {
      json = new TextDecoder().decode(buf);
    }
    return JSON.parse(json) as BackupBundle;
  }

  /** Chiffre une chaîne UTF-8 en AES-GCM avec dérivation PBKDF2. */
  private async encrypt(plaintext: string, password: string): Promise<Uint8Array> {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const key = await this.deriveKey(password, salt);
    const ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      new TextEncoder().encode(plaintext),
    );
    // Format : [salt 16B][iv 12B][ciphertext]
    const out = new Uint8Array(salt.length + iv.length + ciphertext.byteLength);
    out.set(salt, 0);
    out.set(iv, salt.length);
    out.set(new Uint8Array(ciphertext), salt.length + iv.length);
    return out;
  }

  /** Déchiffre un blob produit par encrypt(). */
  private async decrypt(data: Uint8Array, password: string): Promise<string> {
    const salt = data.slice(0, 16);
    const iv = data.slice(16, 28);
    const ciphertext = data.slice(28);
    const key = await this.deriveKey(password, salt);
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
    return new TextDecoder().decode(plain);
  }

  private async deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
    const baseKey = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(password),
      { name: 'PBKDF2' },
      false,
      ['deriveKey'],
    );
    return crypto.subtle.deriveKey(
      { name: 'PBKDF2', salt: new Uint8Array(salt), iterations: 200_000, hash: 'SHA-256' },
      baseKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt'],
    );
  }
}
